import React, { useEffect } from "react";
import { Range, getTrackBackground } from "react-range";
import { CustomizeSliderStyled } from "./CustomizeSliderStyled";
import Flex from "../../../UI/Flex";
import decrement from "../../../assets/decrement.svg";
import increment from "../../../assets/increment.svg";
import { FLEX_ALIGN, JUSTIFY_CONTENT } from "../../../entities/Flex";
import { useSelector } from "react-redux";
import { getDataFromMixture } from "../../Mixture/selectors";
import { useAppDispatch } from "../../../store/store";
import { setSelectedWeight } from "../../Mixture/MixtureSlice";
import { USER_VALUE_DIRECTION } from "../../../entities/Customize";
import { getCalculatedVolume } from "../../Calculation/slice/selectors";
import { toFixedNumber } from "../../../utils/toFixedNumber";
import { getTenantCredentials } from "../../Tenant/selectors";
import { formatCurrency } from "../../../utils/formatCurrency";
import { TEST_ID } from "../../../shared/testid";

const CustomizeSlider: React.FC = () => {
  const dispatch = useAppDispatch();
  const credentials = useSelector(getTenantCredentials);
  const { selectedWeight } = useSelector(getDataFromMixture);
  const { minVolumeOfMix: minWeight, maxVolumeOfMix: maxWeight } =
    useSelector(getCalculatedVolume);
  const onChangeWeight = (type: USER_VALUE_DIRECTION) => {
    if (type === USER_VALUE_DIRECTION.PLUS) {
      const value =
        selectedWeight + 1 > maxWeight ? maxWeight : selectedWeight + 1;
      dispatch(setSelectedWeight(toFixedNumber(value, 2)));
    }

    if (type === USER_VALUE_DIRECTION.MINUS) {
      const value =
        selectedWeight - 1 < minWeight ? minWeight : selectedWeight - 1;
      dispatch(setSelectedWeight(toFixedNumber(value, 2)));
    }
  };

  useEffect(() => {
    if (minWeight > selectedWeight) {
      dispatch(setSelectedWeight(minWeight));
    }
    if (selectedWeight > maxWeight) {
      dispatch(setSelectedWeight(maxWeight));
    }
  }, [selectedWeight, minWeight, maxWeight, dispatch]);

  if (minWeight === 0 || maxWeight === 0) {
    return null;
  }

  return (
    <CustomizeSliderStyled>
      <Flex
        styles={{
          align: FLEX_ALIGN.CENTER,
          justify: JUSTIFY_CONTENT.SPACE_BETWEEN,
        }}
      >
        <div
          className="customize-slider__input"
          data-testid={TEST_ID.CREATE_MIX.SLIDER_DECREMENT}
          onClick={() => onChangeWeight(USER_VALUE_DIRECTION.MINUS)}
        >
          <img src={decrement} alt="Decrement" />
        </div>
        <div className="customize-slider__range">
          <div className="customize-range__labels">
            <span>Min</span>
            <span>Max</span>
          </div>
          <Range
            values={[selectedWeight > minWeight ? selectedWeight : minWeight]}
            step={1}
            min={minWeight}
            max={maxWeight}
            onChange={(values) => dispatch(setSelectedWeight(values[0]))}
            renderTrack={({ props, children }: any) => (
              <div
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                style={{
                  ...props.style,
                  display: "flex",
                  width: "100%",
                }}
              >
                <div
                  ref={props.ref}
                  style={{
                    height: "6px",
                    width: "100%",
                    borderRadius: "4px",
                    background: getTrackBackground({
                      values: [selectedWeight],
                      colors: ["#ABC44E", "#BFBFBF"],
                      min: minWeight,
                      max: maxWeight,
                    }),
                    alignSelf: "center",
                  }}
                >
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ props }: any) => {
              return (
                <div
                  data-testid={TEST_ID.CREATE_MIX.SLIDER_DRAGGABLE}
                  {...props}
                  style={{
                    ...props.style,
                    height: "35px",
                    width: "35px",
                    borderRadius: "50%",
                    backgroundColor: "#FFF",
                    outline: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "3.5px solid #a3a3a3",
                  }}
                />
              );
            }}
          />
          <div className="customize-range__labels">
            <span data-testid={TEST_ID.CREATE_MIX.MIN_VOLUME_VALUE}>
              {formatCurrency({
                amount: minWeight,
                country: credentials?.country,
                amountLabel: "g",
              })}
            </span>
            <span data-testid={TEST_ID.CREATE_MIX.MAX_VOLUME_VALUE}>
              {formatCurrency({
                amount: maxWeight,
                country: credentials?.country,
                amountLabel: "g",
              })}
            </span>
          </div>
        </div>

        <div
          className="customize-slider__input"
          data-testid={TEST_ID.CREATE_MIX.SLIDER_INCREMENT}
          onClick={() => onChangeWeight(USER_VALUE_DIRECTION.PLUS)}
        >
          <img src={increment} alt="Increment" />
        </div>
      </Flex>
    </CustomizeSliderStyled>
  );
};

export default CustomizeSlider;
